import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../config/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

const AuthActionPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode') || '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [invalidCode, setInvalidCode] = useState(false);

  useEffect(() => {
    if (mode !== 'resetPassword' || !oobCode) {
      setInvalidCode(true);
      setLoading(false);
      return;
    }

    verifyPasswordResetCode(auth, oobCode)
      .then((userEmail) => {
        setEmail(userEmail);
        setLoading(false);
      })
      .catch(() => {
        setInvalidCode(true);
        setLoading(false);
      });
  }, [mode, oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess(true);
    } catch (err: any) {
      if (err?.code === 'auth/expired-action-code') {
        setError('Dieser Link ist abgelaufen. Bitte fordere einen neuen an.');
      } else if (err?.code === 'auth/weak-password') {
        setError('Das Passwort ist zu schwach. Bitte wähle ein stärkeres.');
      } else {
        setError(err?.message || 'Passwort konnte nicht gesetzt werden.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-500 to-purple-600 p-5">
      <Card className="max-w-[440px] w-full shadow-2xl">
        <CardContent className="p-12 text-center">
          <div className="text-6xl mb-4">🏡</div>
          <h1 className="text-2xl font-bold mb-2">Holiday Homes der Familie Richner</h1>

          <Separator className="my-8" />

          {loading ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Link wird geprüft...</p>
            </div>
          ) : invalidCode ? (
            <div className="space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold">Ungültiger Link</h2>
              <p className="text-sm text-muted-foreground">
                Dieser Link ist ungültig oder abgelaufen.
              </p>
              <Button variant="outline" onClick={() => navigate('/login')}>
                Zur Anmeldung
              </Button>
            </div>
          ) : success ? (
            <div className="space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold">Passwort gesetzt!</h2>
              <p className="text-sm text-muted-foreground">
                Du kannst dich jetzt mit deiner E-Mail und deinem neuen Passwort anmelden.
              </p>
              <Button onClick={() => navigate('/login')} className="mt-4">
                Jetzt anmelden
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="text-left space-y-4">
              <h2 className="text-lg font-semibold text-center">Passwort festlegen</h2>
              <p className="text-sm text-muted-foreground text-center">
                Lege ein Passwort für <span className="font-medium text-foreground">{email}</span> fest.
              </p>

              <div className="space-y-2">
                <Label htmlFor="new-password">Neues Passwort</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mindestens 6 Zeichen"
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-new-password">Passwort bestätigen</Label>
                <Input
                  id="confirm-new-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Passwort wiederholen"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold"
                disabled={!password || !confirmPassword || submitting}
              >
                {submitting ? 'Wird gespeichert...' : 'Passwort speichern'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthActionPage;
